import { Button } from "@/components/ui/button";
import { ImageIcon, Upload, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import FormHelpText from "../components/FormHelpText";
import { useState, useEffect, useCallback } from "react"; // Import useEffect and useCallback
import { toast } from "sonner";

// Define a type for files with preview URL
interface FileWithPreview extends File {
  preview: string;
}

interface ImageUploadAreaProps {
  files: File[]; // Keep original File objects for upload
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  uploading: boolean;
  uploadProgress: Record<string, number>;
  handleUpload: () => Promise<void>;
  isUploadButtonDisabled: boolean; // Add this prop
}

const ImageUploadArea = ({
  files,
  setFiles,
  uploading,
  uploadProgress,
  handleUpload,
  isUploadButtonDisabled, // Destructure the new prop
}: ImageUploadAreaProps) => {
  const [filesWithPreview, setFilesWithPreview] = useState<FileWithPreview[]>(
    []
  );
  const [isDragging, setIsDragging] = useState(false); // State for drag over effect

  // Effect to create and revoke object URLs for previews
  useEffect(() => {
    const newFilesWithPreview = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setFilesWithPreview(newFilesWithPreview);

    // Cleanup: revoke object URLs when component unmounts or files change
    return () =>
      filesWithPreview.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]); // Re-run effect when the 'files' array changes

  // Handle file selection (from input or drag/drop)
  const handleFiles = useCallback(
    (newlySelectedFiles: FileList | File[]) => {
      if (!newlySelectedFiles || newlySelectedFiles.length === 0) return;

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      const maxFileSize = 5 * 1024 * 1024; // 5MB limit (example)
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/avif",
      ]; // Allowed image types

      Array.from(newlySelectedFiles).forEach((file) => {
        if (!allowedTypes.includes(file.type)) {
          invalidFiles.push(`${file.name} (Invalid type)`);
        } else if (file.size > maxFileSize) {
          invalidFiles.push(`${file.name} (Too large)`);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        toast.warning(`Ignored invalid files: ${invalidFiles.join(", ")}`);
      }

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [setFiles]
  ); // setFiles is stable, but good practice to include

  // Handle file selection from input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset the input so the same file can be selected again if removed
    e.target.value = "";
  };

  // Handle file removal from the list (before upload)
  const handleRemoveFile = useCallback(
    (fileToRemove: File) => {
      // Filter based on file object identity or a unique property if available
      setFiles((prev) => prev.filter((file) => file !== fileToRemove));
    },
    [setFiles]
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent default to allow drop
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files); // Process dropped files
    },
    [handleFiles]
  );

  return (
    <div className="border rounded-md p-4 space-y-4">
      <div className="grid gap-4">
        {/* Drag and drop area */}
        <div
          className={`border-dashed border-2 rounded-lg p-6 text-center transition-colors ${
            isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center space-y-2">
            <ImageIcon className="h-10 w-10 text-gray-400" />
            <div className="text-sm text-gray-500">
              Drag and drop images here or click to browse
            </div>
            {/* Hidden file input */}
            <Input
              id="image-upload"
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById("image-upload")?.click()}
            >
              Select Files
            </Button>
          </div>
        </div>

        {/* Selected files list with previews */}
        {filesWithPreview.length > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              {" "}
              {/* Added items-center */}
              <h4 className="text-sm font-medium">
                Selected Files ({filesWithPreview.length})
              </h4>{" "}
              {/* Show count */}
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleUpload}
                disabled={isUploadButtonDisabled} // Use the new prop here
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? "Uploading..." : "Upload All"}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {" "}
              {/* Grid for previews */}
              {filesWithPreview.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="relative group border rounded-md overflow-hidden"
                >
                  {" "}
                  {/* Added border and overflow */}
                  {/* Image Preview */}
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="h-24 w-full object-cover" // Fixed size for preview
                    // No onError needed for local URLs
                  />
                  {/* File name and progress */}
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-xs p-1">
                    {" "}
                    {/* Overlay for name/progress */}
                    <span className="truncate w-full text-center">
                      {file.name}
                    </span>
                    {uploading &&
                      uploadProgress[file.name] !== undefined &&
                      uploadProgress[file.name] !== 0 && ( // Check for undefined and not 0
                        <span className="mt-1">
                          {uploadProgress[file.name] === -1
                            ? "Error"
                            : `${uploadProgress[file.name]}%`}{" "}
                          {/* Show Error or progress */}
                        </span>
                      )}
                  </div>
                  {/* Remove button */}
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon" // Icon button size
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6" // Position and size
                    onClick={() => handleRemoveFile(file)} // Pass file object to remove
                    disabled={uploading}
                  >
                    <Trash2 className="h-3 w-3" /> {/* Smaller icon */}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <FormHelpText>
          Upload at least 3 clear images of your property. The first image will
          be used as the cover image. Maximum file size: 5MB. Allowed types:
          JPG, PNG, GIF, WEBP, AVIF.
        </FormHelpText>
      </div>
    </div>
  );
};

export default ImageUploadArea;
